import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useWorkspace } from '../context/WorkspaceContext.jsx';
import { t, useI18n } from '../i18n/index.jsx';
import { IconMenu, IconClose } from './icons.jsx';
import NotificationBell from './NotificationBell.jsx';
import CommandPalette from './CommandPalette.jsx';
import WorkspaceSwitch from './WorkspaceSwitch.jsx';

function getInitials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useI18n();
  const { navGroups, workspace, isFunds } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Tag the shell for workspace-specific theming (subtle accent on brand).
  useEffect(() => {
    document.documentElement.dataset.workspace = workspace;
    return () => { delete document.documentElement.dataset.workspace; };
  }, [workspace]);

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  }

  function go(path) {
    setMenuOpen(false);
    setOpen(false);
    navigate(path);
  }

  return (
    <div className={`shell workspace-${workspace}`}>
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="brand">
          <span className="brand-mark">A</span>
          <div className="brand-text">
            <span className="brand-name">Agevolami</span>
            <span className="brand-workspace muted">
              {isFunds ? t('Italy — Funds') : t('Morocco — Trade')}
            </span>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <IconClose width={18} height={18} />
          </button>
        </div>

        {/* Mobile: workspace switch under brand */}
        <div className="sidebar-workspace">
          <WorkspaceSwitch />
        </div>

        <nav>
          {navGroups.map((g, gi) => (
            <div className="nav-group" key={gi}>
              {g.label && <div className="nav-group-label">{t(g.label)}</div>}
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setOpen(false)}
                  title={t(n.label)}
                >
                  <n.Icon className="nav-icon" />
                  <span className="nav-label">{t(n.label)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot sidebar-foot-compact">
          <div className="avatar sm-avatar">{getInitials(user?.name)}</div>
          <div className="who">
            <div className="who-name">{user?.name}</div>
            <div className="who-role">{user?.role}</div>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="hamburger" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            <IconMenu />
          </button>

          {/* Desktop: switch sits in the open center of the topbar */}
          <div className="topbar-workspace">
            <WorkspaceSwitch />
          </div>

          <div className="spacer" />

          <button
            type="button"
            className="btn ghost sm topbar-search-hint"
            onClick={() => window.dispatchEvent(new Event('agevolami:cmdk'))}
            title={`${t('Search')} (Ctrl+K)`}
          >
            <span className="muted sm">Ctrl+K</span>
          </button>

          <NotificationBell />

          <div className="lang-switch topbar-lang" role="group" aria-label="Language">
            <button
              type="button"
              className={`chip xs ${lang === 'it' ? 'on' : ''}`}
              onClick={() => setLang('it')}
            >IT</button>
            <button
              type="button"
              className={`chip xs ${lang === 'en' ? 'on' : ''}`}
              onClick={() => setLang('en')}
            >EN</button>
          </div>

          <div className="user-menu" ref={menuRef}>
            <button
              type="button"
              className={`user-pill user-pill-btn ${menuOpen ? 'open' : ''}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <div className="avatar">{getInitials(user?.name)}</div>
              <span className="muted user-email">{user?.email}</span>
              <span className="user-caret" aria-hidden>▾</span>
            </button>
            {menuOpen && (
              <div className="user-dropdown" role="menu">
                <div className="user-dropdown-head">
                  <div className="who-name">{user?.name}</div>
                  <div className="who-role">{user?.role}</div>
                </div>
                <button type="button" role="menuitem" onClick={() => go('/account')}>{t('Account')}</button>
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <button type="button" role="menuitem" onClick={() => go('/settings')}>{t('Settings')}</button>
                )}
                {user?.role === 'admin' && (
                  <button type="button" role="menuitem" onClick={() => go('/users')}>{t('Users')}</button>
                )}
                <div className="user-dropdown-sep" />
                <button type="button" role="menuitem" className="danger" onClick={handleLogout}>{t('Sign out')}</button>
              </div>
            )}
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>

      {open && <div className="backdrop" onClick={() => setOpen(false)} />}
      <CommandPalette />
    </div>
  );
}
